import { useEffect } from "react";

import { useAuthState } from "../context/auth";
import { useLiveQuery } from "dexie-react-hooks";
import { db } from "../data/Database";
import { useDataLoader } from "../data/DataLoader";
import { useAnalyticsState } from "../context/analytics";

export const useChartData = (group, range, fieldId, field = undefined) => {
  const user = useAuthState();
  const dataLoaderObject = { user: null };
  if (field) {
    dataLoaderObject.user = user;
    dataLoaderObject.field = field;
  }
  const { loadData } = useDataLoader(dataLoaderObject);
  const {
    range: { start_date, end_date },
  } = useAnalyticsState();

  let timer;
  useEffect(() => {
    clearTimeout(timer);
    timer = setTimeout(() => loadData({ start_date, end_date }), 200);
  }, [start_date, end_date, fieldId]);

  // Use range from chart (i.e. parameter range) as it's in local time for display
  const chartData = useLiveQuery(async () => {
    if (group === "parameter_types") {
      return (await db.parameter_types.toArray()) ?? [];
    }
    if (group === "parameters") {
      return (
        (await db[group ?? ""]?.where("field_id").equals(fieldId).toArray()) ??
        []
      );
    }

    // values to load the last value before the start date for
    const persist =
      group === "agronomics" ? ["ph_target", "ec_target", "drain_target"] : [];
    const persisted = [];
    for (const p of persist) {
      const v = await db[group ?? ""]
        ?.where("field_id")
        .equals(fieldId)
        .and((item) => item.timestamp < range.start_date.valueOf())
        .and((item) => item[p] !== undefined && item[p] !== null)
        .last();
      if (v) {
        persisted.push(v);
      }
    }
    persisted.sort((a, b) => a.timestamp - b.timestamp);

    const base =
      (await db[group ?? ""]
        ?.where(["field_id", "timestamp"])
        .between(
          [fieldId, range.start_date.valueOf()],
          [fieldId, range.end_date.valueOf()]
        )
        .toArray()) ?? [];
    return [...persisted, ...base];
  }, [start_date, end_date, fieldId]);

  const reloadData = () => {
    loadData({ start_date, end_date }, 200);
  };

  return { chartData, reloadData };
};

export const useReloadChartData = () => {
  const {
    range: { start_date, end_date },
  } = useAnalyticsState();

  const reloadData = () => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { loadData } = useDataLoader({ user: null });
    loadData({ start_date, end_date }, true);
  };

  return { reloadData };
};
